import { useContext, useMemo, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { EventSections } from '../../lib/events/EventLogger';
import DraggableMenu from '../downloads/DraggableMenu';
import EasyInfo from '../EasyInfo';
import ImportStatusItem from './ImportStatusItem';
import { ConcernsContext } from '../../lib/context/ConcernsContext';
import { ImportStatusSummary } from '../smartsend/SmartSendSocketsModels';
import ConvertConcernsStateToImportStatusSummary from './ConvertConcernsStateToImportStatusSummary';
import { IsSocketMessageBad } from './IsSocketMessageBad';
import { P2GChip, P2GLink, P2GBox, P2GGrid, P2GText } from '@parcel2go/core-ui';
import useGoto from '../../../../hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import { AppCard } from '~/components/card/app-card';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

const ImportStatus: React.FC = () => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [detached, setDetached] = useLocalStorage<boolean>('importStatusIsOpen', false);
  const [open, setOpen] = useState(detached);
  const ctx = useContext(ConcernsContext);
  const { goTo } = useGoto();
  const { track } = useSmartSendAnalytics();

  const onPinned = () => {
    setDetached(true);
    track({
      type: 'import_status_pinned',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const onUnPinned = () => {
    setDetached(false);
    track({
      type: 'import_status_unpinned',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const onContentShow = () => {
    setOpen(true);
    track({
      type: 'import_status_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const onContentHide = () => {
    setOpen(false);
  };

  const socketSummaries: ImportStatusSummary[] = useMemo(
    () => (open ? ConvertConcernsStateToImportStatusSummary(ctx.state) : []),
    [ctx.state.Sockets, ctx.state.Accounts, open]
  );

  const isError: boolean = useMemo(
    () => !!ctx.state.Sockets?.find((s) => IsSocketMessageBad(s.Details)),
    [ctx.state.Sockets]
  );

  const label = isError ? (
    <P2GChip size="small" label={t('IMPORT_PROGRESS_ISSUES', { defaultValue: 'Import Issues' })} color="error" />
  ) : ctx.state.Sockets?.length == 0 ? (
    <P2GChip
      size="small"
      label={
        ctx.state.Accounts.length === 0
          ? t('IMPORT_PROGRESS_NO_ACCOUNTS', { defaultValue: 'eCommerce Imports' })
          : t('IMPORT_PROGRESS_QUEUED', { defaultValue: 'Imports Queued' })
      }
      color="primary"
    />
  ) : (
    <P2GChip
      size="small"
      label={t('IMPORT_PROGRESS_COMPLETE', { defaultValue: 'Imports Completed' })}
      color="success"
    />
  );

  return (
    <DraggableMenu
      detached={detached}
      onPinned={onPinned}
      onUnPinned={onUnPinned}
      onContentShow={onContentShow}
      onContentHide={onContentHide}
      palette="primary"
      id="ImportStatus"
      classNames="btn-importstatus"
      title="Account Status"
      width="400px"
      badgeContent={0}
      control={label}
      additionalLinks={[
        <P2GLink
          onClick={() => {
            track({
              type: 'import_status_link_account_click',
              payload: {
                ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
              }
            });
            goTo(RoutingConstants.AccountLinking);
          }}
        >
          Link an Account
        </P2GLink>
      ]}
    >
      {socketSummaries.length > 0 ? (
        <P2GBox pt={1} pb={1}>
          <P2GGrid container direction="column" gap={1}>
            {socketSummaries.map((x) => (
              <P2GGrid item key={x.MPAccountId}>
                <AppCard>
                  <P2GBox p={1}>
                    <ImportStatusItem group={x} />
                  </P2GBox>
                </AppCard>
              </P2GGrid>
            ))}
          </P2GGrid>
        </P2GBox>
      ) : (
        <>
          <P2GBox bordered>
            <P2GBox p={2}>
              <EasyInfo>
                <P2GText variant="body2" component="p">
                  The status of your linked accounts will show here while we import your items.
                </P2GText>
              </EasyInfo>
            </P2GBox>
          </P2GBox>
        </>
      )}
    </DraggableMenu>
  );
};

export default ImportStatus;
